import { useState, useMemo } from "react";
import { Trophy, MapPin, Search, Crown, Star } from "lucide-react";
import { Reveal, SectionHeader } from "@/components/shared/Reveal";
import { Input } from "@/components/ui/input";
import { RECENT, LEGENDS, WINNERS, EARLY_MILESTONES } from "@/data/winners";

const RecentCard = ({ w, i }) => (
  <Reveal delay={i * 0.08}>
    <div className="group relative h-full overflow-hidden rounded-2xl border border-white/8 bg-[#14161A] p-7 hover-lift hover:border-amber-500/50">
      <div className="flex items-center justify-between">
        <span className="font-display text-5xl font-black text-amber-500/90 leading-none">{w.year}</span>
        <Trophy className="w-8 h-8 text-amber-500/40 group-hover:text-amber-500 transition-colors" />
      </div>
      <h3 className="mt-5 font-display text-3xl font-black uppercase text-white leading-none">{w.name}</h3>
      <div className="mt-1 font-mono text-xs uppercase tracking-widest text-white/50">{w.club}</div>
      <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-white/60"><MapPin className="w-4 h-4 text-amber-500" /> {w.place}</div>
      <p className="mt-4 pt-4 border-t border-white/8 text-sm text-white/60 leading-relaxed">{w.note}</p>
    </div>
  </Reveal>
);

const LegendCard = ({ l, i }) => {
  const top = i === 0;
  return (
    <Reveal delay={i * 0.04}>
      <div className={`relative h-full rounded-xl border p-5 hover-lift ${top ? "border-amber-500/60 bg-amber-500/10 amber-glow" : "border-white/8 bg-[#14161A] hover:border-amber-500/40"}`}>
        <div className="flex items-start justify-between">
          <div className="font-display text-6xl font-black leading-none text-amber-500">{l.wins}</div>
          {top ? <Crown className="w-6 h-6 text-amber-400" /> : <Star className="w-5 h-5 text-white/25" />}
        </div>
        <div className="mt-3 font-display text-xl font-bold uppercase text-white leading-tight">{l.name}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{l.wins} segrar{l.era ? ` · ${l.era}` : ""}</div>
        {l.extra && <p className="mt-2 text-xs text-white/55 leading-relaxed">{l.extra}</p>}
      </div>
    </Reveal>
  );
};

export const WinnersSection = () => {
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return WINNERS;
    return WINNERS.filter((w) => String(w.year).includes(s) || w.name.toLowerCase().includes(s));
  }, [q]);

  const decades = useMemo(() => {
    const map = {};
    filtered.forEach((w) => {
      const d = Math.floor(w.year / 10) * 10;
      (map[d] = map[d] || []).push(w);
    });
    return Object.entries(map).sort((a, b) => b[0] - a[0]);
  }, [filtered]);

  return (
    <>
      {/* Recent winners */}
      <section data-testid="winners-section" className="relative py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><SectionHeader kicker="Vinnare" title="Segrare senaste åren" center /></Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {RECENT.map((w, i) => <RecentCard key={w.year} w={w} i={i} />)}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-8 rounded-2xl border border-amber-500/25 bg-amber-500/5 p-6 text-center">
              <p className="text-white/80 text-base sm:text-lg">
                <Crown className="inline w-5 h-5 text-amber-500 -mt-1 mr-1" />
                Senast Kåsan gick i <span className="text-amber-500 font-bold">Knivsta (2016)</span> vann
                <span className="text-white font-bold"> Joakim Ljunggren</span> sin åttonde kåsaseger.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Legends */}
      <section data-testid="legends-section" className="relative py-16 sm:py-24 bg-[#0A0B0D]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><SectionHeader kicker="Legends of the Kåsa" title="Kåsans största vinnare" subtitle="Joakim Ljunggren är ensam rekordhållare med nio segrar – och den ende som erövrat tre vandringskåsor." center /></Reveal>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {LEGENDS.map((l, i) => <LegendCard key={l.name} l={l} i={i} />)}
          </div>
        </div>
      </section>

      {/* Year-by-year archive */}
      <section data-testid="winners-archive" className="relative py-16 sm:py-24">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal><SectionHeader kicker="Arkiv" title="Vinnare år för år" subtitle="Modern historik 1950–2025." /></Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                data-testid="winners-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Sök förare eller år..."
                className="pl-9 bg-[#14161A] border-white/15 text-white placeholder:text-white/30"
              />
            </div>
          </Reveal>

          <div className="mt-10 space-y-10">
            {decades.length === 0 && <p className="font-mono text-sm text-white/40">Inga träffar.</p>}
            {decades.map(([decade, items]) => (
              <Reveal key={decade}>
                <div>
                  <h3 className="font-display text-2xl font-black uppercase text-amber-500 mb-4">{decade}-tal</h3>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {items.map((w) => {
                      const none = w.status === "none";
                      return (
                        <div
                          key={w.year}
                          className={`flex items-center gap-4 rounded-lg border px-4 py-3 ${
                            w.highlight ? "border-amber-500/60 bg-amber-500/10"
                            : none ? "border-white/5 bg-white/[0.02]"
                            : "border-white/8 bg-[#14161A]"
                          }`}
                        >
                          <span className={`font-mono text-lg font-bold tabular-nums w-14 shrink-0 ${w.highlight ? "text-amber-400" : none ? "text-white/30" : "text-white/50"}`}>{w.year}</span>
                          <span className={`flex-1 ${none ? "text-white/40 text-sm italic" : "text-white font-semibold"}`}>
                            {w.name} {w.flag && <span className="ml-1">{w.flag}</span>}
                            {w.place && !none && <span className="text-amber-500/80 text-sm font-normal"> · {w.place}</span>}
                          </span>
                          {w.highlight && <Crown className="w-4 h-4 text-amber-400 shrink-0" />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Early era 1915-1949 */}
      <section data-testid="early-era" className="relative py-16 sm:py-24 bg-[#0A0B0D]">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal><SectionHeader kicker="Pionjäråren" title="1915–1949" subtitle="Reglerna var annorlunda – flera förare kunde ta en inteckning samma år genom prickfri körning. Därför saknas en enkel resultatlista från den äldsta eran." /></Reveal>
          <div className="mt-10 relative border-l-2 border-white/10 pl-8 space-y-6">
            {EARLY_MILESTONES.map((m, i) => (
              <Reveal key={m.year} delay={i * 0.03}>
                <div className="relative">
                  <span className="absolute -left-[41px] top-1.5 w-3.5 h-3.5 rounded-full bg-amber-500 ring-4 ring-[#0A0B0D]" />
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                    <span className="font-display text-2xl font-black text-amber-500/90 w-28 shrink-0">{m.year}</span>
                    <span className="text-white/70">{m.text}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};
